import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DownloadDonePage.css';
import {
  DownloadURLsMap,
  getDownloadLinks,
  getSystemVersion,
} from './getDownloadLinks';

const DownloadDonePage = () => {
  const { t } = useTranslation();
  const preferredVersion = useMemo(() => getSystemVersion(), []);
  const [downloadUrlMap, setDownloadUrlMap] = useState<DownloadURLsMap | null>(
    null
  );

  const nameMap: Record<string, string> = useMemo(
    () => ({
      'mac-arm': t('common.platforms.macAppleSilicon'),
      'mac-intel': t('common.platforms.macIntel'),
      win: t('common.platforms.windows'),
    }),
    [t]
  );

  useEffect(() => {
    const getDownloadLinksAndSetState = async () => {
      const downloadLinks = await getDownloadLinks();
      setDownloadUrlMap(downloadLinks);
    };

    getDownloadLinksAndSetState();
  }, [setDownloadUrlMap]);

  const preferredDownloadLink = downloadUrlMap?.[preferredVersion] ?? null;
  const otherDownloadLinks =
    downloadUrlMap &&
    Object.entries(downloadUrlMap)
      .map(([version, url]) => {
        if (version === preferredVersion) {
          return null;
        }
        return (
          <a
            key={version}
            id={`electric-desktop-app-download-from-done-page-${version}`}
            href={url}
            target="_blank"
            rel="noreferrer">
            {t('downloadDone.downloadFor', { platform: nameMap[version] })}
          </a>
        );
      })
      .filter(Boolean);

  return (
    <main className="page-container">
      <img
        className="logo"
        alt={t('common.electricLogo')}
        src="/assets/images/logo_electric_white.png"
      />
      <section className="download-done-content">
        <div>
          <h1 className="download-done-h1 download-done-text">
            {t('downloadDone.title')}
          </h1>
          <h1 className="download-done-h1 download-done-text">
            {t('downloadDone.next')}
          </h1>
          <div className="numbered-steps-wrapper">
            <div>
              <div>
                <div className="num-circle">1</div>
              </div>
              <p className="download-done-text">{t('downloadDone.step1')}</p>
            </div>
            <div>
              <div>
                <div className="num-circle">2</div>
              </div>
              <p className="download-done-text">{t('downloadDone.step2')}</p>
            </div>
            <div>
              <div>
                <div className="num-circle">3</div>
              </div>
              <p className="download-done-text">
                {t('downloadDone.step3Start')}{' '}
                <a
                  id="knowledge-base-install-app-instructions-link"
                  href="https://support.electric.ai/electricithub/electric-it-hub-installing-the-desktop-app"
                  target="_blank"
                  rel="noreferrer">
                  {t('downloadDone.step3Link')}
                </a>{' '}
                {t('downloadDone.step3End')}
              </p>
            </div>
          </div>
          {preferredDownloadLink && (
            <p className="download-done-text small">
              {t('downloadDone.notWorking')}{' '}
              <a
                id={`electric-desktop-app-download-from-done-page-${preferredVersion}`}
                href={preferredDownloadLink}
                target="_blank"
                rel="noreferrer">
                {t('downloadDone.restartDownload', {
                  platform: nameMap[preferredVersion],
                })}
              </a>
            </p>
          )}
          <p className="download-done-text small">
            {t('downloadDone.needAnotherVersion')} {otherDownloadLinks?.[0]}{' '}
            {t('downloadDone.or')} {otherDownloadLinks?.[1]}
          </p>
        </div>
        <div>
          <img
            src="/assets/images/laptop-app-features.png"
            alt={t('common.laptopWithIcons')}
            width="600px"
          />
        </div>
      </section>
    </main>
  );
};

export default DownloadDonePage;
